import { useMemo } from 'react';

import {
  PrimaryButton,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@procurify/ui'
import classNames from 'classnames'

import { SpendVariance } from '@webapp/components/SpendVariance'
import { TableLoader } from '@webapp/components/TableLoader'
import { type CustomWidthOption } from '@webapp/components/TableLoader/TableLoader'
import {
  useFeatureFlag,
  useProcurifyIntl,
  useFormatBudgetValues,
} from '@webapp/hooks'
import { type BudgetCategory } from '@webapp/models/BudgetCategory'
import budgetCategorySVG from './assets/budget-category.svg'
import messages from './messages'
import { BudgetCategoryListStyled } from './styles/BudgetCategoryListStyled'

export interface BudgetCategoryListItem extends BudgetCategory {
  isOverBudget?: boolean
}

export interface IBudgetCategoryListProps {
  items?: BudgetCategoryListItem[]
  onEmptyStateButtonClick?: () => void
  onRowClick?: (budgetCategory: BudgetCategoryListItem) => void
  selectedId?: BudgetCategory['id']
  isLoading?: boolean
}

export const BudgetCategoryList = ({
  items = [],
  isLoading = false,
  onEmptyStateButtonClick = () => undefined,
  onRowClick = () => undefined,
  selectedId,
}: IBudgetCategoryListProps) => {
  const intl = useProcurifyIntl()

  const FT_5817_ENABLE_BUDGETS_REDESIGN = useFeatureFlag(
    'FT_5817_ENABLE_BUDGETS_REDESIGN'
  )

  const { formatBudgetPercent, formatBudgetPrice } = useFormatBudgetValues()

  const columns: CustomWidthOption[] = useMemo(() => {
    return FT_5817_ENABLE_BUDGETS_REDESIGN
      ? [
          { width: 150 },
          { width: 150, alignRight: true },
          { width: 70, alignRight: true },
        ]
      : [{ width: 150 }, { width: 70, alignRight: true }]
  }, [FT_5817_ENABLE_BUDGETS_REDESIGN])

  const hasBudgets = items.length > 0

  return (
    <BudgetCategoryListStyled>
      <Table
        tableBodyCellPadding={16}
        aria-label={intl.formatMessage(
          FT_5817_ENABLE_BUDGETS_REDESIGN
            ? messages.tableAriaLabel
            : messages.oldTableAriaLabel
        )}
        className={classNames({
          'budget-list': FT_5817_ENABLE_BUDGETS_REDESIGN,
        })}
      >
        <TableHead>
          <TableRow>
            <TableCell>{intl.formatMessage(messages.nameHeader)}</TableCell>

            {FT_5817_ENABLE_BUDGETS_REDESIGN ? (
              <>
                <TableCell align='right' width={columns[1]?.width}>
                  {intl.formatMessage(messages.remainingHeader)}
                </TableCell>
                <TableCell align='right' width={columns[2]?.width}>
                  {intl.formatMessage(messages.percentageHeader)}
                </TableCell>
              </>
            ) : (
              <TableCell align='right'>
                {intl.formatMessage(messages.varianceHeader)}
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        {isLoading && <TableLoader rows={10} widths={columns} />}

        {!isLoading && !hasBudgets && (
          <TableBody zebra={false} hoverable={false}>
            <TableRow>
              <TableCell colSpan={columns.length + 1}>
                <EmptyState
                  header={intl.formatMessage(
                    FT_5817_ENABLE_BUDGETS_REDESIGN
                      ? messages.emptyStateHeader
                      : messages.oldEmptyStateHeader
                  )}
                  content={intl.formatMessage(messages.emptyStateContent)}
                  button={
                    <PrimaryButton
                      icon='add'
                      onClick={() => onEmptyStateButtonClick()}
                    >
                      {intl.formatMessage(
                        FT_5817_ENABLE_BUDGETS_REDESIGN
                          ? messages.emptyStateButtonText
                          : messages.oldEmptyStateButtonText
                      )}
                    </PrimaryButton>
                  }
                  imgUrl={budgetCategorySVG}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        )}

        {!isLoading && hasBudgets && (
          <TableBody zebra={false}>
            {items.map((budget) => {
              return (
                <TableRow
                  key={budget.uuid}
                  onClick={() => onRowClick(budget)}
                  selected={budget.id === selectedId}
                  className={classNames('budget', {
                    'budget--overage': budget.isOverBudget,
                  })}
                >
                  <TableCell>{budget.name}</TableCell>

                  {FT_5817_ENABLE_BUDGETS_REDESIGN ? (
                    <>
                      <TableCell className='budget--amount' align='right'>
                        {formatBudgetPrice(budget.variance)}
                      </TableCell>
                      <TableCell className='budget--amount' align={'right'}>
                        {formatBudgetPercent(budget.variance_percentage)}
                      </TableCell>
                    </>
                  ) : (
                    <TableCell align='right'>
                      <SpendVariance
                        variance={budget.variance}
                        variancePercentage={budget.variance_percentage}
                        isOverBudget={budget.isOverBudget}
                      />
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        )}
      </Table>
    </BudgetCategoryListStyled>
  )
}
